/**
 * Capaciteitsplanning per dagdeel — Types (Epic C-1 — Fundament, Story C-1.1)
 *
 * Eigen, groeiend domein (Epic C-2..C-5 bouwen erop voort) — eigen bestand,
 * zelfde `types/`+`schemas/`-split-conventie als `types/coreTasks.ts` /
 * `schemas/coreTasks.ts` (AD-18).
 *
 * `planning_tasks` is bewust NIET gekoppeld aan `core_tasks`/`tasks` (AD-10):
 * dit is een apart, eigen concept ("planningstaak" voor het dagelijkse
 * capaciteitsplanbord), geen relatie met de kerntakencatalogus.
 *
 * Story C-3.1 breidt dit bestand uit met het board-read-model
 * (`PlanningBoardResponse` e.a.) — hergebruikt bewust `AttendanceDagdeelStatus`/
 * `AttendanceCalendarScope` uit `types/calendar.js` (AD-13: één statusvocabulaire).
 */
export const PLANNING_TASK_COLORS = [
    { hex: '#2F5A7A', label: 'Navy' },
    { hex: '#54949E', label: 'Teal' },
    { hex: '#0D9488', label: 'Smaragd' },
    { hex: '#166534', label: 'Bosgroen' },
    { hex: '#1D4ED8', label: 'Koningsblauw' },
    { hex: '#0369A1', label: 'Staalblauw' },
    { hex: '#A21CAF', label: 'Magenta' },
    { hex: '#9D174D', label: 'Framboos' },
    { hex: '#334155', label: 'Leisteen' },
    { hex: '#3F6212', label: 'Olijfgroen' },
];
/**
 * Elke activiteit ontleed in werksoort × fase.
 *
 * Dit is geen administratie om de administratie: de doelmarkt-regel hangt aan
 * de WERKSOORT en niet aan de fase, en die afleiding is hier de enige bron.
 * Bewijs uit de bronmatrix zelf: in het valideren-blok staan doelmarkten bij
 * wie vastleggingen valideert, en géén doelmarkt bij wie alleen controles
 * valideert — dezelfde regel die het hele hercontrole-blok zonder doelmarkt
 * laat staan.
 *
 * LET OP bij wijzigen: een activiteit die van werksoort verandert, is een
 * DATAMIGRATIE en niet alleen een codewijziging. Van markt-loos naar
 * markt-plichtig laat rijen met `target_market_key = 0` achter die in geen
 * enkele marktkolom zichtbaar zijn — niet aan te vinken, niet in te trekken.
 * Andersom vinden de lookups (die op `?? 0` normaliseren) de rijen met een
 * echte markt nooit meer, en maakt een grant een duplicaat. Schrijf dus een
 * backfill in dezelfde wijziging.
 */
export const PLANNING_TASK_ACTIVITY_DECOMPOSITION = {
    vastleggen: { werksoort: 'vastleggen', fase: 'uitvoeren' },
    controle: { werksoort: 'controle', fase: 'uitvoeren' },
    hercontrole: { werksoort: 'hercontrole', fase: 'uitvoeren' },
    valideren_vastleggen: { werksoort: 'vastleggen', fase: 'valideren' },
    valideren_controle: { werksoort: 'controle', fase: 'valideren' },
    valideren_hercontrole: { werksoort: 'hercontrole', fase: 'valideren' },
};
/**
 * Kent deze activiteit een doelmarkt?
 *
 * Alleen vastleggen is marktafhankelijk. (Her)controle is de GS1
 * Controles-dienstverlening, die alleen voor Nederland bestaat — daar is een
 * doelmarkt geen ontbrekende waarde maar een niet-bestaand begrip.
 */
export function activityUsesTargetMarket(activity) {
    return PLANNING_TASK_ACTIVITY_DECOMPOSITION[activity].werksoort === 'vastleggen';
}
/**
 * De ENIGE normalisatie van een doelmarkt-id naar een vergelijkbare sleutel.
 *
 * Twee valkuilen die hetzelfde probleem zijn: in SQL is `kolom = NULL` nooit
 * waar, en in JavaScript is `${null}` niet `${0}`. Beide laten een markt-loze
 * kwalificatie stil door de mazen vallen — de database maakt dan duplicaten en
 * de UI toont een bestaande kwalificatie als leeg vakje. De database gebruikt
 * hiervoor de gegenereerde kolom `target_market_key`; alle code die een
 * sleutel bouwt of vergelijkt, gebruikt deze functie. Nooit rechtstreeks op
 * `targetMarketId` vergelijken.
 */
export function targetMarketKey(id) {
    return id ?? 0;
}
/**
 * NL-weergavelabels (Epic C-2) — één bron voor de matrix-sectiekoppen (C-2.2),
 * de taak-triple-weergave (C-2.3) en het taak-formulier-select (C-2.3), zodat
 * niemand de vocabulaire opnieuw als losse string-literal typt. De labels zijn
 * letterlijk de taaknamen van het roosterblad.
 */
export const PLANNING_TASK_ACTIVITY_LABELS = {
    vastleggen: 'Vastleggen',
    controle: 'Controle',
    hercontrole: 'Hercontrole',
    valideren_vastleggen: 'Valideren vastleggen',
    valideren_controle: 'Valideren controle',
    valideren_hercontrole: 'Valideren hercontrole',
};
/**
 * De afdeling waar de kwalificatiematrix standaard op filtert.
 *
 * Als NAAM en niet als id, om twee redenen. Een id verschilt per omgeving en
 * zegt niets bij het lezen; en de faalmodus van een naam is zacht — wordt hij
 * niet gevonden (hernoemd, of een omgeving zonder die afdeling), dan valt het
 * filter zichtbaar terug op "alle afdelingen" in plaats van een lege lijst te
 * tonen. Het is een STANDAARD, geen grens: elke afdeling blijft kiesbaar.
 */
export const STANDAARD_KWALIFICATIE_AFDELING = 'Data Verwerking';
//# sourceMappingURL=planning.js.map